import { ArrowLeft01Icon, ArrowRight01Icon } from '@hugeicons/core-free-icons';
import { type ReactNode, useState } from 'react';
import { Icon } from '@/components/primitives/icon';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';

const PAGE_SIZES = [10, 25, 50, 100] as const;

/** More rows on the server than are loaded: the next batch of a keyset list. */
export interface LoadMore {
  /**
   * Which list this is (its action and filters). A new key is a new list,
   * so the table goes back to its first page.
   */
  key?: string;
  /** Every row that matches, when the server counts them. */
  total?: number;
  hasMore: boolean;
  /** Fetches the next batch and says how many rows are loaded after it. */
  load: () => Promise<{ loaded: number; hasMore: boolean }>;
}

export interface Paging<T> {
  /** This page's rows; undefined while the list is loading. */
  visible: T[] | undefined;
  page: number;
  pages: number;
  size: number;
  total: number;
  goTo: (page: number) => void;
  changeSize: (size: number) => void;
}

/**
 * Splits a loaded list into pages. Always call it before any early return.
 * A new `listKey` (other filters) starts again at page 1: the page a reader
 * had reached in the old list means nothing in the new one.
 */
export function usePaging<T>(
  rows: readonly T[] | undefined,
  initialSize = 25,
  listKey?: string,
): Paging<T> {
  const [size, setSize] = useState(initialSize);
  const [requested, setRequested] = useState(1);
  const [pagedKey, setPagedKey] = useState(listKey);
  if (pagedKey !== listKey) {
    setPagedKey(listKey);
    setRequested(1);
  }
  const total = rows?.length ?? 0;
  const pages = Math.max(1, Math.ceil(total / size));
  // Fewer rows than before (a filter, a refresh) must not leave an empty page.
  const page = Math.min(requested, pages);
  return {
    visible: rows?.slice((page - 1) * size, page * size),
    page,
    pages,
    size,
    total,
    goTo: setRequested,
    changeSize: (next) => {
      setSize(next);
      setRequested(1);
    },
  };
}

/**
 * Fetches batches one after another (each needs the last one's cursor)
 * until `wanted` rows are loaded or there are no more.
 */
async function loadUntil(
  more: LoadMore,
  have: number,
  wanted: number,
): Promise<void> {
  if (have >= wanted) return;
  const next = await more.load();
  if (next.hasMore) await loadUntil(more, next.loaded, wanted);
}

/** 1 … 4 5 6 … 20: the first, the last, and the pages around this one. */
function pageWindow(
  page: number,
  pages: number,
): { key: string; page: number | null }[] {
  const wanted = new Set(
    [1, pages, page - 1, page, page + 1].filter((n) => n >= 1 && n <= pages),
  );
  const sorted = [...wanted].sort((a, b) => a - b);
  const out: { key: string; page: number | null }[] = [];
  for (const [index, n] of sorted.entries()) {
    if (index > 0 && n - (sorted[index - 1] ?? 0) > 1) {
      out.push({ key: `gap-before-${n}`, page: null });
    }
    out.push({ key: `page-${n}`, page: n });
  }
  return out;
}

/**
 * The strip under a table: "Showing 1–25 of 1,284", the page numbers, and how
 * many rows a page holds. It shows for every list with rows, so a short list
 * still says how many there are. `extra` sits before the page numbers (the
 * density switch).
 */
export function TablePager<T>({
  paging,
  more,
  extra,
}: Readonly<{ paging: Paging<T>; more?: LoadMore; extra?: ReactNode }>) {
  const [loading, setLoading] = useState(false);
  const { page, size, total: loaded } = paging;
  if (loaded === 0) return null;
  // The server's count when it sends one; otherwise what is loaded so far.
  const total = Math.max(more?.total ?? loaded, loaded);
  const pages = Math.max(paging.pages, Math.ceil(total / size));
  const first = (page - 1) * size + 1;
  const last = Math.min(page * size, total);
  const hasMore = Boolean(more?.hasMore);
  // A page past what is loaded fetches the batches up to it first.
  const goTo = async (target: number) => {
    setLoading(true);
    try {
      if (more && hasMore) await loadUntil(more, loaded, target * size);
    } finally {
      setLoading(false);
    }
    paging.goTo(target);
  };
  return (
    <div className="flex flex-col items-center gap-3 border-t p-3 sm:flex-row sm:justify-between">
      <p
        aria-live="polite"
        className="text-sm text-muted-foreground tabular-nums"
      >
        Showing {first}–{last} of {total.toLocaleString()}
        {hasMore && more?.total === undefined ? '+' : ''}
      </p>
      <div className="flex flex-wrap items-center justify-center gap-2">
        {extra}
        {pages > 1 ? (
          <nav aria-label="Pages" className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => void goTo(page - 1)}
            >
              <Icon icon={ArrowLeft01Icon} size={14} />
              <span className="sr-only sm:not-sr-only">Previous</span>
            </Button>
            {pageWindow(page, pages).map((entry) =>
              entry.page === null ? (
                <span
                  key={entry.key}
                  aria-hidden="true"
                  className="px-1 text-muted-foreground"
                >
                  …
                </span>
              ) : (
                <Button
                  key={entry.key}
                  variant={entry.page === page ? 'secondary' : 'ghost'}
                  size="sm"
                  aria-current={entry.page === page ? 'page' : undefined}
                  className="min-w-7 tabular-nums"
                  onClick={() => void goTo(entry.page ?? 1)}
                >
                  {entry.page}
                </Button>
              ),
            )}
            <Button
              variant="outline"
              size="sm"
              disabled={page === pages}
              pending={loading}
              onClick={() => void goTo(page + 1)}
            >
              <span className="sr-only sm:not-sr-only">Next</span>
              <Icon icon={ArrowRight01Icon} size={14} />
            </Button>
          </nav>
        ) : null}
        <Select
          aria-label="Rows per page"
          className="w-28"
          value={String(size)}
          onChange={(event) => paging.changeSize(Number(event.target.value))}
        >
          {PAGE_SIZES.map((option) => (
            <option key={option} value={option}>
              {option} rows
            </option>
          ))}
        </Select>
      </div>
    </div>
  );
}
